import React, { memo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import { formatMoneySmart, formatPointsCompact } from '../utils/money';
import LevelBadge from '../components/LevelBadge';
import RewardsDropdown from '../components/RewardsDropdown';
import ActiveBuffs from '../components/ActiveBuffs';
import PointsIcon from '../components/PointsIcon';
import { useBalance } from '../contexts/BalanceContext';
import { usePlayer } from '../contexts/PlayerContext';
import { useChatToggle } from '../contexts/ChatContext';

import { useBuffs } from '../contexts/BuffsContext';
import casinoWS from '../services/websocket';

const TUTORIAL_HIGHLIGHT_STYLE = { position: 'relative', zIndex: 100002 };

// Icon components
const Icons = {
  User: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
    </svg>
  ),
  Chat: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
    </svg>
  ),
};

const TopBar = () => {
  const { t } = useTranslation('common');
  const { balance } = useBalance();
  const {
    playerName,
    avatarUrl,
    level,
    points,
  } = usePlayer();
  const { chatOpen, handleChatToggle: onChatToggle } = useChatToggle();
  const { activeBuffs, pendingBuffs, handleActivateBuff: onActivateBuff } = useBuffs();

  const [canSpinReward, setCanSpinReward] = useState(false);

  // Check daily reward eligibility on mount and periodically
  useEffect(() => {
    const checkRewardState = () => {
      if (casinoWS.isConnected) {
        casinoWS.dailyRewardGetState();
      }
    };

    const unsubState = casinoWS.on('dailyRewardState', (data) => {
      setCanSpinReward(data.canSpin);
    });

    const unsubResult = casinoWS.on('dailyRewardSpinResult', () => {
      setCanSpinReward(false);
    });

    const unsubConnected = casinoWS.on('init', () => {
      setTimeout(checkRewardState, 500);
    });

    setTimeout(checkRewardState, 1000);
    const interval = setInterval(checkRewardState, 60000);

    return () => {
      unsubState();
      unsubResult();
      unsubConnected();
      clearInterval(interval);
    };
  }, []);

  return (
    <header className="top-bar">
        <div className="top-left">
          <div className="logo-container">
            <NavLink to="/" className="logo">
              <img src="/games/spinbox_logo.png" alt="S&Sino" className="logo-image" style={{ height: 32 }} />
            </NavLink>
            <span className="logo-badge">{t('beta')}</span>
          </div>
        </div>

        <div className="top-center">
          {/* Search box hidden for now */}
        </div>

        <div className="top-right">
          {/* Active Buffs */}
          <ActiveBuffs buffs={activeBuffs} />

          {/* Level Badge */}
          <LevelBadge level={level} />

          {/* Points Display */}
          <div className="points-display">
            <PointsIcon size={16} color="#f59e0b" />
            <span className="points-amount">{formatPointsCompact(points)}</span>
          </div>

          {/* Cash/Balance Display */}
          <div className="balance-display cash">
            <span className="amount">{formatMoneySmart(balance)}</span>
          </div>

          <RewardsDropdown
            canSpin={canSpinReward}
            pendingBuffs={pendingBuffs}
            onActivateBuff={onActivateBuff}
          />
          <button className="header-btn user-btn">
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt={playerName}
                className="user-avatar"
                onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'block'; }}
              />
            ) : null}
            <span className="user-icon-fallback" style={{ display: avatarUrl ? 'none' : 'block' }}>
              <Icons.User />
            </span>
          </button>
          <button
            className={`chat-toggle-btn ${chatOpen ? 'active' : ''}`}
            onClick={onChatToggle}
            title={t('toggle_chat')}
          >
            <Icons.Chat />
          </button>
        </div>
      </header>
  );
};

export default memo(TopBar);
